import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { demenageurUploadUrl } from '../../utils/demenageurUploadUrl';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiAlertCircle,
  FiMail,
  FiPhone,
  FiUser,
  FiTruck,
  FiShield,
  FiRefreshCw,
  FiImage,
} from 'react-icons/fi';

function formatServiceType(value) {
  if (!value) return '—';
  const v = String(value).toLowerCase();
  if (v === 'local') return 'Local';
  if (v === 'international') return 'International';
  return String(value);
}

function DocumentCard({ label, filename, side, srcOverride }) {
  const [broken, setBroken] = useState(false);
  const src = srcOverride || demenageurUploadUrl(filename);
  if (!src) return null;
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900/60 overflow-hidden">
      <div className="px-3 py-2 bg-gray-800/80 border-b border-gray-700 text-xs font-semibold text-gray-400">
        {label} — {side}
      </div>
      <a href={src} target="_blank" rel="noopener noreferrer" className="block">
        {broken ? (
          <div className="min-h-[120px] flex flex-col items-center justify-center text-gray-400 text-sm p-4 gap-2">
            <FiImage className="w-8 h-8 opacity-50" />
            <span>Aperçu indisponible</span>
            <span className="text-yellow-500/90 text-xs break-all">{src}</span>
          </div>
        ) : (
          <img
            src={src}
            alt={`${label} ${side}`}
            className="w-full max-h-72 object-contain bg-black/40"
            onError={() => setBroken(true)}
          />
        )}
      </a>
    </div>
  );
}

function SinglePhotoSection({ title, filename, url }) {
  if (!filename && !url) {
    return (
      <div className="rounded-xl border border-dashed border-gray-600 p-6 text-gray-500 text-sm">
        {title} : aucune photo enregistrée.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-white flex items-center gap-2">
        <FiTruck className="text-yellow-500" />
        {title}
      </h3>
      <div className="grid grid-cols-1 max-w-lg">
        <DocumentCard label={title} filename={filename} side="Photo" srcOverride={url} />
      </div>
    </div>
  );
}

function DocumentSection({ title, doc, labels }) {
  if (!doc || typeof doc !== 'object') {
    return (
      <div className="rounded-xl border border-dashed border-gray-600 p-6 text-gray-500 text-sm">
        {title} : aucun fichier enregistré.
      </div>
    );
  }
  const front = doc.front || doc.recto;
  const back = doc.back || doc.verso;
  const frontUrl = doc.frontUrl || doc.rectoUrl;
  const backUrl = doc.backUrl || doc.versoUrl;
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-white flex items-center gap-2">
        <FiShield className="text-yellow-500" />
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {front && <DocumentCard label={title} filename={front} side={labels.front} srcOverride={frontUrl} />}
        {back && <DocumentCard label={title} filename={back} side={labels.back} srcOverride={backUrl} />}
      </div>
    </div>
  );
}

export default function MoverRegistrationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.getMoverRegistrationDetails(id);
      if (res?.success && res.data) {
        setPayload(res.data);
      } else {
        throw new Error(res?.message || 'Chargement impossible');
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Erreur');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVerify = async () => {
    if (!id || !payload?.user) return;
    if (!window.confirm('Valider ce compte déménageur ?')) return;
    try {
      setVerifying(true);
      const res = await api.verifyDemenageurAccount(id);
      if (res?.success) {
        await load();
        navigate('/admin/account-verification');
      } else {
        throw new Error(res?.message || 'Échec');
      }
    } catch (e) {
      alert(e.message || 'Erreur');
    } finally {
      setVerifying(false);
    }
  };

  const user = payload?.user;
  const profile = payload?.profile;
  const vehiclePhotoFilename =
    payload?.vehiclePhoto ||
    profile?.vehicle_photo ||
    payload?.user?.registration_vehicle_photo ||
    null;
  const vehiclePhotoUrl =
    payload?.vehiclePhotoUrl ||
    profile?.vehicle_photo_url ||
    demenageurUploadUrl(vehiclePhotoFilename);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white p-6 lg:p-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <Link
            to="/admin/account-verification"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-800 border border-gray-600 text-gray-200 hover:border-yellow-600/50 hover:text-yellow-400 text-sm font-medium"
          >
            <FiArrowLeft />
            Retour à la vérification
          </Link>
          {user && !user.is_verified && (
            <button
              type="button"
              disabled={verifying}
              onClick={handleVerify}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-600 to-yellow-500 text-gray-900 font-bold text-sm disabled:opacity-60"
            >
              {verifying ? <FiRefreshCw className="animate-spin" /> : <FiCheckCircle />}
              Valider le compte
            </button>
          )}
        </div>

        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <FiTruck className="text-yellow-500 w-8 h-8" />
          Fiche d&apos;inscription déménageur
        </h1>
        <p className="text-gray-400 mb-8">
          Données et pièces fournies à l&apos;inscription (consultation admin).
        </p>

        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="p-4 rounded-xl bg-red-900/30 border border-red-700/50 text-red-300 mb-6">{error}</div>
        )}

        {!loading && user && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
              <div className="rounded-2xl border border-gray-700 bg-gray-900/50 p-6 space-y-4">
                <h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
                  <FiUser /> Identité
                </h2>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500">Prénom</dt>
                    <dd className="text-white font-medium">{user.first_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Nom</dt>
                    <dd className="text-white font-medium">{user.last_name || '—'}</dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiMail className="text-gray-500" />
                    <div>
                      <dt className="text-gray-500 text-xs">Email</dt>
                      <dd className="text-white">{user.email || '—'}</dd>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiPhone className="text-gray-500" />
                    <div>
                      <dt className="text-gray-500 text-xs">Téléphone</dt>
                      <dd className="text-white">{user.phone || '—'}</dd>
                    </div>
                  </div>
                  <div>
                    <dt className="text-gray-500">Mot de passe</dt>
                    <dd className="text-amber-200/90 text-sm leading-relaxed">{user.passwordInfo}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Compte vérifié</dt>
                    <dd>
                      {user.is_verified ? (
                        <span className="inline-flex items-center gap-1 text-green-400">
                          <FiCheckCircle /> Oui
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-400">
                          <FiAlertCircle /> En attente
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Statut</dt>
                    <dd className="text-white">{user.status || '—'}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-2xl border border-gray-700 bg-gray-900/50 p-6 space-y-4">
                <h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
                  <FiTruck /> Profil métier
                </h2>
                {profile ? (
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-gray-500">Type de service</dt>
                      <dd className="text-white">{formatServiceType(profile.service_type)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Type de véhicule</dt>
                      <dd className="text-white">{profile.vehicle_type || '—'}</dd>
                    </div>
                    {profile.country != null && profile.country !== '' && (
                      <div>
                        <dt className="text-gray-500">Pays (international)</dt>
                        <dd className="text-white">{String(profile.country)}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">N° CIN / pièce</dt>
                      <dd className="text-white font-mono">{profile.cin_number || '—'}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-gray-500 text-sm">Aucune ligne mover_profiles.</p>
                )}
              </div>
            </div>

            <div className="space-y-10">
              <SinglePhotoSection
                title="Photo du camion"
                filename={vehiclePhotoFilename}
                url={vehiclePhotoUrl}
              />
              <DocumentSection
                title="Permis de conduire"
                doc={user.permis}
                labels={{ front: 'Recto', back: 'Verso' }}
              />
              <DocumentSection
                title="Carte d’identité"
                doc={user.carte_cin}
                labels={{ front: 'Recto', back: 'Verso' }}
              />
              <DocumentSection
                title="Carte grise"
                doc={user.carte_grise}
                labels={{ front: 'Recto', back: 'Verso' }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
